import React, { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Typography,
  CircularProgress,
  IconButton,
  Drawer,
  FormControl,
  Select,
  MenuItem,
  Pagination,
  Grid,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import Header from '../components/Header';
import FilterDrawer from '../components/FilterDrawer';
import SaleCard from '../components/SaleCard';
import { useSales } from '../hooks/useSales';
import { useApp } from '../context/AppContext';
import { Sale, SaleType, SourceWebsite, SALE_TYPE_LABELS } from '../types';

const inferSaleType = (title: string | null, sourceWebsite: string | null): SaleType => {
  const titleLower = title?.toLowerCase() || '';
  const sourceLower = sourceWebsite?.toLowerCase() || '';
  
  if (titleLower.includes('estate') || sourceLower.includes('estate')) {
    return 'estate_sale';
  }
  if (titleLower.includes('multi-family') || titleLower.includes('multi family')) {
    return 'multi_family';
  }
  if (titleLower.includes('moving')) {
    return 'moving_sale';
  }
  if (titleLower.includes('fundraiser')) {
    return 'fundraiser';
  }
  
  return 'yard_sale';
};

const ITEMS_PER_PAGE = 10;

const SearchResultsScreen: React.FC = () => {
  const { selectedCity, setSelectedCity, favorites, toggleFavorite } = useApp();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'relevance'>('relevance');
  const [page, setPage] = useState(1);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<{
    saleType: SaleType | 'all';
    dateRange: 'all' | 'this_weekend' | 'this_week' | 'upcoming';
    sourceWebsite: SourceWebsite | 'all';
  }>({
    saleType: 'all',
    dateRange: 'all',
    sourceWebsite: 'all',
  });

  const { data: sales, isLoading, error } = useSales({ city: selectedCity });

  const filteredAndSortedSales = useMemo(() => {
    if (!sales) return [];

    let filtered = [...sales];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (sale) =>
          sale.title?.toLowerCase().includes(query) ||
          sale.address?.toLowerCase().includes(query) ||
          sale.description?.toLowerCase().includes(query)
      );
    }

    // Apply sale type filter
    if (activeFilters.saleType !== 'all') {
      filtered = filtered.filter((sale) => {
        const saleType = inferSaleType(sale.title, sale.source_website);
        return saleType === activeFilters.saleType;
      });
    }

    // Apply date range filter
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    switch (activeFilters.dateRange) {
      case 'this_weekend': {
        const dayOfWeek = today.getDay();
        const saturday = new Date(today);
        saturday.setDate(today.getDate() + (6 - dayOfWeek));
        const sunday = new Date(saturday);
        sunday.setDate(saturday.getDate() + 1);
        
        filtered = filtered.filter((sale) => {
          const saleStart = new Date(sale.start_date);
          return saleStart >= saturday && saleStart <= sunday;
        });
        break;
      }
      case 'this_week': {
        const nextWeek = new Date(today);
        nextWeek.setDate(today.getDate() + 7);
        
        filtered = filtered.filter((sale) => {
          const saleStart = new Date(sale.start_date);
          return saleStart >= today && saleStart <= nextWeek;
        });
        break;
      }
      case 'upcoming': {
        filtered = filtered.filter((sale) => {
          const saleStart = new Date(sale.start_date);
          return saleStart >= today;
        });
        break;
      }
    }

    // Apply source website filter
    if (activeFilters.sourceWebsite !== 'all') {
      filtered = filtered.filter((sale) => sale.source_website === activeFilters.sourceWebsite);
    }

    // Apply sorting
    if (sortBy === 'date') {
      filtered.sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
    }

    return filtered;
  }, [sales, searchQuery, sortBy, activeFilters]);

  const paginatedSales = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredAndSortedSales.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredAndSortedSales, page]);

  const totalPages = Math.ceil(filteredAndSortedSales.length / ITEMS_PER_PAGE);

  const handleFilterApply = (filters: { saleType: SaleType | 'all'; dateRange: 'all' | 'this_weekend' | 'this_week' | 'upcoming'; sourceWebsite: SourceWebsite | 'all' }) => {
    setActiveFilters(filters);
    setFilterDrawerOpen(false);
    setPage(1);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (activeFilters.saleType !== 'all') count++;
    if (activeFilters.dateRange !== 'all') count++;
    return count;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default' }}>
      <Header title="Search" showBackButton={false} />
      
      {/* Search and Filter Bar */}
      <Box sx={{ p: 2, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            placeholder="Search sales..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ 
              borderRadius: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />
          <IconButton 
            onClick={() => setFilterDrawerOpen(true)}
            sx={{ 
              bgcolor: getActiveFilterCount() > 0 ? 'primary.main' : 'grey.100',
              color: getActiveFilterCount() > 0 ? 'white' : 'text.primary',
              '&:hover': {
                bgcolor: getActiveFilterCount() > 0 ? 'primary.dark' : 'grey.200',
              }
            }}
          >
            <FilterListIcon />
          </IconButton>
        </Box>

        {/* Active Filters */}
        {getActiveFilterCount() > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            {activeFilters.saleType !== 'all' && (
              <Chip 
                label={SALE_TYPE_LABELS[activeFilters.saleType]} 
                size="small"
                onDelete={() => setActiveFilters({ ...activeFilters, saleType: 'all' })}
              />
            )}
            {activeFilters.dateRange !== 'all' && (
              <Chip 
                label={activeFilters.dateRange.replace('_', ' ')} 
                size="small"
                onDelete={() => setActiveFilters({ ...activeFilters, dateRange: 'all' })}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Sort and Results Count */}
      <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'background.paper' }}>
        <Typography variant="body2" color="text.secondary">
          {filteredAndSortedSales.length} results
        </Typography>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'date' | 'relevance')}
            sx={{ fontSize: 14 }}
          >
            <MenuItem value="relevance">Relevance</MenuItem>
            <MenuItem value="date">Date</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Results Grid */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="error">Error loading sales</Typography>
          </Box>
        ) : paginatedSales.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">No sales found matching your criteria</Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {paginatedSales.map((sale) => (
              <Grid item xs={12} sm={6} key={sale.id}>
                <SaleCard 
                  sale={sale}
                  isFavorite={favorites.includes(sale.id)}
                  onFavoriteToggle={(id) => toggleFavorite(id)}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ p: 2, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'center' }}>
          <Pagination 
            count={totalPages} 
            page={page} 
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Filter Drawer */}
      <FilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        onApply={handleFilterApply}
        activeFilters={activeFilters}
      />
    </Box>
  );
};

export default SearchResultsScreen;
